import { observer } from 'mobx-react-lite';
import * as R from 'ramda';
import React from 'react';
import { withDefaultProps } from 'react-default-props-context';
import { GoToNextExplanationButton } from 'src/answers/components/GoToNextExplanationButton';
import { ExplanationLinkT } from 'src/api/types/ExplanationLinkT';
import { ExplanationT } from 'src/api/types/ExplanationT';
import { Portal } from 'src/frames/components/Portal';
import { useScrollToTop } from 'src/frames/components/useScrollToTop';
import { ForwardArrowIcon } from 'src/frames/icons/ForwardArrowIcon';
import { cn } from 'src/frames/utils/cn';
import { BackButton } from 'src/questions/components/BackButton';
import './ExplanationView.scss';
import { trackEvent } from '../../tracking/utils/initTracking';

export type PropsT = {
  className?: any;
};

type DefaultPropsT = {
  explanation: ExplanationT;
  explanationPageIdx: number;
  riskfactors: ExplanationT[];
  tips: ExplanationT[];
  goToExplanationPage: (idx: number) => void;
  portalElm: any;
};

export const ExplanationView: React.FC<PropsT> = observer(
  withDefaultProps<PropsT, DefaultPropsT>((props: PropsT & DefaultPropsT) => {
    useScrollToTop('SelfTestView', 'ExplanationView');

    const label =
      props.explanation.category === 'RISKFACTOR' ? 'Risikofaktor' : 'Tipp';

    const nr =
      props.explanation.category === 'RISKFACTOR'
        ? props.explanationPageIdx + 1
        : props.explanationPageIdx - props.riskfactors.length + 1;

    const hasNextExplanation =
      props.explanationPageIdx + 1 <
      props.riskfactors.length + props.tips.length;

    const getName = (siteName: ExplanationLinkT['siteName'], url: ExplanationLinkT['url']) => {
      if (siteName && siteName.trim() !== '') {
        return siteName;
      }

      try {
        let hostname = new URL(url).hostname;

        // If the link belongs to any of Netdoktor domains: de, ch or at
        if (/^(www.|)netdoktor\.(ch|de|at)$/.test(hostname)) {
          return 'NetDoktor';
        }

        if (/^(www.|)mylife\.de$/.test(hostname)) {
          return 'MyLife';
        }

        hostname = hostname.split('.')[hostname.split('.').length - 2];

        // Since the result of the above line for links like "http://dfdf" is undefined,
        //  it is better to check if it is not undefined
        return hostname ? hostname : '';
      } catch {
        return ''; // The given link is not started with a 'http' or it is not a valid link, so we can't create an object of URL
      }
    };

    const linkDivs = R.map((link: ExplanationLinkT) => {
      return (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className={cn('ExplanationView__Link', 'flex flex-col', 'px-4 my-2')}
          onClick={() => {
            const name = ['NetDoktor', 'MyLife'].includes(getName(link.siteName, link.url)) ? 'click_link_internal' : 'click_link_external';
            trackEvent(name, 'Result', link.url);
          }}
        >
          <div className="ExplanationView__LinkSuperTitle">
            {getName(link.siteName, link.url)}
          </div>
          <div className="flex flex-row justify-between">
            {link.title}
            <ForwardArrowIcon />
          </div>
        </a>
      );
    }, props.explanation.links);

    return (
      <div className={cn('ExplanationView', 'flex flex-col', props.className)}>
        <Portal elm={props.portalElm}>
          <BackButton className="mr-6" />
          <div className={cn('ExplanationView__SuperTitleInPortal', 'flex-1')}>
            {label} {nr}
          </div>
          <GoToNextExplanationButton
            nextExplanationPageIdx={props.explanationPageIdx + 1}
            className={cn('ml-6', { invisible: !hasNextExplanation })}
          />
        </Portal>
        <div
          className={cn('ExplanationView__Card', 'flex flex-col', 'px-4 py-2')}
        >
          <div className={cn('ExplanationView__Title')}>
            {props.explanation.title}
          </div>
          <div className={cn('ExplanationView__Body', 'mt-2')}>
            {props.explanation.text}
          </div>
          {linkDivs.length > 0 && (
            <div className={cn('ExplanationView__SubTitle', 'mt-6')}>
              Lesen Sie hier mehr
            </div>
          )}
          {linkDivs}
        </div>
      </div>
    );
  })
);
